import "animate.css";
import AppShell from "../components/navigation/AppShell";
import { Provider } from "react-redux";
import { store } from "./store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import QueueBoard from "../components/QueueBoard/MainContainer";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import LoginForm from "../components/authentication/login";
import InternalServerError from "../components/error/InternalErrorPage";
import { SnackbarProvider } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { ThemeModeProvider } from "../components/common/theme/theme-context";
import { DialogProvider } from "../components/common/context/TrackMuiDialogContext";
import { useIdleTimer } from "../components/common/hooks/useIdleTimer";
import { initAmplitude } from "../components/common/amplitude/amplitude";
import { LoadingProvider } from "../components/common/context/LoadingContext";
import { useAuth } from "./hooks/useAuth";
import ErrorFallback from "../components/error/ErrorFallback";
import EmbeddedAutoLogin from "../components/authentication/EmbeddedAutoLogin";
import Logistics from "../components/logistics/logistics";
import TechnicianCalendar from "../components/technicianCalendar/TechnicianCalendar";
import { IntakeCalendarPage } from "../components/intakeCalendar/IntakeCalendarPage";
import { NetworkStatusProvider } from "components/common/network/NetworkStatusProvider";

// Create a QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Component to handle idle timer with route checking
const IdletimerHandler = () => {
  const location = useLocation();

  //To track the idle state of the application and refresh it after certain time
  // Memoize the idle callback
  const handleIdle = useCallback(() => {
    // Don't refresh if user is on Logistics, TechnicianCalendar, IntakeCalendar, or Login page
    if (
      location.pathname === "/Logistics" ||
      location.pathname === "/TechnicianCalendar" ||
      location.pathname === "/IntakeCalendar" ||
      location.pathname === "/" ||
      location.pathname === "/Login"
    ) {
      //console.log('Idle timer triggered but user is on an exempt page - skipping refresh');
      return;
    }

    //console.log('Idle timer triggered - refreshing application');
    //alert("Session timed out. Refreshing...");
    window.location.reload(); // or handle logout
  }, [location.pathname]);

  useIdleTimer(handleIdle); // Pass the memoized callback

  return null; // This component doesn't render anything
};

export const App = () => {
  //Zustand
  //const fetchWorkOrders = useWorkOrderStore((state) => state.fetchWorkOrders);
  const { checkAuthentication } = useAuth();
  const [authenticated, setAuthenticated] = useState(() =>
    checkAuthentication(),
  );

  // Listen for authentication changes (e.g., logout)
  useEffect(() => {
    const handleAuthChange = () => {
      setAuthenticated(checkAuthentication());
    };
    window.addEventListener("storage", handleAuthChange);
    window.addEventListener("userChanged", handleAuthChange);
    return () => {
      window.removeEventListener("storage", handleAuthChange);
      window.removeEventListener("userChanged", handleAuthChange);
    };
  }, [checkAuthentication]);
  initAmplitude();

  const SecureRoute: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    const isAuthenticated = checkAuthentication();

    if (!isAuthenticated) {
      // Clear any stale authentication data
      localStorage.removeItem("authToken");
      localStorage.removeItem("username");
      return <Navigate to="/" replace />;
    }

    return <>{children}</>;
  };

  useEffect(() => {
    const wasRefreshed = sessionStorage.getItem("wasRefreshed");

    if (wasRefreshed) {
      sessionStorage.removeItem("wasRefreshed");
      //This is the place where I want to track it
    }

    window.addEventListener("beforeunload", () => {
      sessionStorage.setItem("wasRefreshed", "true");
    });

    return () => {
      window.removeEventListener("beforeunload", () => {
        sessionStorage.setItem("wasRefreshed", "true");
      });
    };
  }, []);
  //return <FilterPanel1></FilterPanel1>;
  return (
    <>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, componentStack) => {
          console.log("Error Boundary", error);
        }}
      >
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <ThemeModeProvider>
              <SnackbarProvider
                autoHideDuration={3000}
                maxSnack={3}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <DialogProvider>
                  <LoadingProvider>
                    <Router>
                      <NetworkStatusProvider>
                        <IdletimerHandler />
                        <Routes>
                        <Route
                          path="/"
                          element={
                            authenticated ? (
                              <Navigate to="/QueueBoard" replace />
                            ) : (
                              <LoginForm setAuthenticated={setAuthenticated} />
                            )
                          }
                        />
                        <Route
                          path="/EmbeddedAutoLogin"
                          element={<EmbeddedAutoLogin />}
                        />
                        <Route
                          path="/QueueBoard"
                          element={
                            <SecureRoute>
                              <AppShell>
                                <QueueBoard />
                              </AppShell>
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/Logistics"
                          element={
                            <SecureRoute>
                              <AppShell>
                                <Logistics />
                              </AppShell>
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/TechnicianCalendar"
                          element={
                            <SecureRoute>
                              <AppShell>
                                <TechnicianCalendar />
                              </AppShell>
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/IntakeCalendar"
                          element={
                            <SecureRoute>
                              <AppShell>
                                <IntakeCalendarPage />
                              </AppShell>
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/error"
                          element={<InternalServerError />}
                        />
                        <Route
                          path="*"
                          element={
                            authenticated ? (
                              <Navigate to="/QueueBoard" replace />
                            ) : (
                              <Navigate to="/" replace />
                            )
                          }
                        />
                        </Routes>
                      </NetworkStatusProvider>
                    </Router>
                  </LoadingProvider>
                </DialogProvider>
              </SnackbarProvider>
            </ThemeModeProvider>
          </Provider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
};

export default App;
